// Single source of truth for redirect-URL construction across the auth flow.
//
// The auth FE has THREE distinct redirect concerns that used to be implemented
// inline in 5+ places, with subtly different (sometimes buggy) behavior:
//
//   1. Mid-chain navigation between auth-FE pages
//      (signin-new → corp-selection → legal-entity-selection → workspace-selection)
//      — needs to forward the `?redirect=` param so the LAST page knows the final
//      destination. Falling back to DEFAULT_REDIRECT_URL here drops information.
//
//   2. Final redirect OUT of the auth FE (to HRMS or wherever)
//      — the terminal hop. This is the ONLY place that should fall back to
//      DEFAULT_REDIRECT_URL when no `?redirect=` was in URL.
//
//   3. Embedding the access token in the URL
//      — required when target is localhost (HttpOnly cookies on .veytan.com
//      aren't visible to localhost FEs). Forbidden when target is deployed
//      (token in URL leaks to logs / referrer headers).

/**
 * Whether the redirect target is a localhost dev origin. Used to gate
 * inclusion of the access token in the URL — we only put the token in URLs
 * when the target is localhost (where HttpOnly cookies on `.veytan.com`
 * aren't visible and the receiving FE needs the token to bootstrap auth).
 * For any deployed target, the token must NEVER appear in the URL.
 */
export const isLocalTarget = (url: string): boolean =>
  /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$|\?)/i.test(url);

/**
 * Preserves the `?redirect=` query parameter when navigating between
 * auth-FE pages (signin → corp-selection → legal-entity-selection →
 * workspace-selection). Used in soft-nav (react-router) where the next
 * page needs to know the final destination.
 *
 * If current URL has `?redirect=...`, append it to targetPath. Otherwise
 * return targetPath bare — DO NOT fall back to a default URL mid-chain
 * (the final hop in `buildFinalRedirect` handles that).
 */
export const preserveRedirectInPath = (
  targetPath: string,
  location: { search: string }
): string => {
  const params = new URLSearchParams(location.search);
  const redirectParam = params.get('redirect');
  return redirectParam
    ? `${targetPath}?redirect=${encodeURIComponent(redirectParam)}`
    : targetPath;
};

/**
 * Builds the FINAL `window.location.href` URL after workspace selection
 * (or any other terminal redirect that takes the user out of the auth FE).
 *
 * - Appends `?token=` ONLY when target is localhost (security gate).
 * - Appends `?workspace_data=` when `workspaceData` is provided.
 * - Caller is responsible for resolving the redirect target URL (typically
 *   `?redirect=` query param || PARAMS.DEFAULT_REDIRECT_URL).
 *
 * Replaces inline construction in:
 *   - workspace-selection/page.tsx getRedirectUrl
 *   - legal-entity-selection/page.tsx no-staffing branch
 *   - api.ts parent-corp auto-redirect
 */
export const buildFinalRedirect = (
  redirectUrl: string,
  opts: { token?: string; workspaceData?: any } = {}
): string => {
  let finalUrl = redirectUrl;

  // Token: localhost-only, never on deployed.
  if (opts.token && isLocalTarget(finalUrl)) {
    const sep = finalUrl.includes('?') ? '&' : '?';
    finalUrl = `${finalUrl}${sep}token=${opts.token}`;
  }

  // Workspace data: routing info, safe on any origin.
  if (opts.workspaceData) {
    const sep = finalUrl.includes('?') ? '&' : '?';
    const workspaceParam = encodeURIComponent(
      JSON.stringify(opts.workspaceData)
    );
    finalUrl = `${finalUrl}${sep}workspace_data=${workspaceParam}`;
  }

  return finalUrl;
};
